import React, { useEffect, useState } from 'react';
import { Product, fetchProductImage } from 'utils/grocyApi.ts';
import './ProductImage.css';

interface ProductImageProps {
  product?: Product;
  imageUrl?: string;
  className?: string;
  alt?: string;
  size?: 'small' | 'medium' | 'large';
  width?: number;
}

const ProductImage: React.FC<ProductImageProps> = ({
  product,
  imageUrl,
  className = '',
  alt,
  size = 'medium',
  width = 120,
}) => {
  const [imageError, setImageError] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [isBlobUrl, setIsBlobUrl] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const handleImageError = () => {
    setImageError(true);
    setImageLoading(false);
  };

  // Cleanup blob URL when it changes or on unmount
  useEffect(() => {
    return () => {
      if (isBlobUrl && resolvedUrl) {
        URL.revokeObjectURL(resolvedUrl);
      }
    };
  }, [resolvedUrl, isBlobUrl]);

  useEffect(() => {
    let didCancel = false;

    const load = async () => {
      setImageLoading(true);
      setImageError(false);

      // Prefer explicit imageUrl if provided
      if (imageUrl) {
        setResolvedUrl(imageUrl);
        setIsBlobUrl(false);
        // Keep loading true; onLoad of <img> will flip to false
        return;
      }

      if (product) {
        try {
          const url = await fetchProductImage(product, width);
          if (!didCancel) {
            if (url) {
              setResolvedUrl(url);
              setIsBlobUrl(true);
              // We already fetched the blob; let onLoad confirm rendering, but end loading to avoid double spinner
              setImageLoading(false);
            } else {
              setResolvedUrl(null);
              setIsBlobUrl(false);
              setImageError(true);
              setImageLoading(false);
            }
          }
        } catch {
          if (!didCancel) {
            setResolvedUrl(null);
            setIsBlobUrl(false);
            setImageError(true);
            setImageLoading(false);
          }
        }
        return;
      }

      // No source provided
      setResolvedUrl(null);
      setIsBlobUrl(false);
      setImageError(true);
      setImageLoading(false);
    };

    load();

    return () => {
      didCancel = true;
    };
  }, [product, imageUrl, width]);

  const sizeClass = `product-image--${size}`;
  const altText = alt || product?.name || 'Product image';

  return (
    <div className={`product-image ${sizeClass} ${className}`}>
      {resolvedUrl && (
        <img
          src={resolvedUrl}
          alt={altText}
          className={`product-image__img ${imageLoading ? 'product-image__img--loading' : ''}`}
          onError={handleImageError}
          onLoad={() => setImageLoading(false)}
          style={{ display: imageError ? 'none' : 'block' }}
        />
      )}
      {imageLoading && !imageError && (
        <div className="product-image__placeholder product-image__loading">⏳</div>
      )}
      {(!resolvedUrl || imageError) && !imageLoading && (
        <div className="product-image__placeholder">📦</div>
      )}
    </div>
  );
};

export default ProductImage;